/**
 * Chat Input Component
 * Provides the input interface for sending messages to the agent
 */
import {
  Suspense,
  lazy,
  useCallback,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent
} from "react";
import { Trash } from "lucide-react";
import type { tools } from "@/tools";
import { isToolUIPart, type ChatStatus } from "ai";
import type { UIMessage } from "@ai-sdk/react";
import type { FeatureFlag } from "@/shared/feature-flags";

// AI Elements imports
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  type PromptInputMessage,
  PromptInputSelect,
  PromptInputSelectContent,
  PromptInputSelectItem,
  PromptInputSelectTrigger,
  PromptInputSelectValue,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools
} from "@/components/ai-elements/prompt-input";

import { AVAILABLE_MODELS } from "@/shared/models";
import { PromptAttachmentsDisplay } from "@/components/chat/composer/PromptAttachmentsDisplay";
import {
  invokePolicyContextClear,
  isPolicyContextAvailable,
  POLICY_REVIEW_ACTIVE_LABEL
} from "@/components/chat/policy-mode-indicator";

// List of tools that require human confirmation
const toolsRequiringConfirmation: (keyof typeof tools)[] = [
  "createTask",
  "updateTask",
  "updateClaimStatus"
];

const FeatureFlagsDebug = lazy(async () => {
  const mod = await import("@/components/FeatureFlagsDebug");
  return { default: mod.FeatureFlagsDebug };
});

const ManualToolCallsDebug = lazy(async () => {
  const mod = await import("@/components/ManualToolCallsDebug");
  return { default: mod.ManualToolCallsDebug };
});

const DebugTimelineToggle = lazy(async () => {
  const mod = await import("@/components/debug/DeveloperTraceDrawer");
  return { default: mod.DebugTimelineToggle };
});

export function isChatBusyStatus(status: ChatStatus): boolean {
  return status === "submitted" || status === "streaming";
}

interface ChatInputProps {
  /** Current selected model */
  model: string;
  /** Function to change the model */
  onModelChange: (value: string) => void;
  /** Handler for form submission */
  onSubmit: (
    message: PromptInputMessage,
    event?: FormEvent<HTMLFormElement>
  ) => void | Promise<void>;
  /** Current agent status */
  status: ChatStatus;
  /** Stop an in-flight model response */
  onStop: () => void;
  /** Array of messages (for checking pending confirmations) */
  messages: UIMessage[];
  /** Function to clear chat history */
  onClearHistory: () => void;
  /** Function to check if a feature is enabled */
  isEnabled: (flagId: FeatureFlag) => boolean;
  /** Whether to show debug controls (development mode) */
  showDebug?: boolean;
  /** Debug hook to manually trigger a tool call */
  onManualToolCall?: (
    toolName: string,
    args: Record<string, unknown>
  ) => Promise<void>;
  /** Whether the debug drawer is open */
  isDebugDrawerOpen?: boolean;
  /** Toggle debug drawer visibility */
  onToggleDebugDrawer?: () => void;
  /** Whether policy context is currently available */
  policyContextAvailable?: boolean;
  /** Clear policy context action from app container */
  onClearPolicyContext?: () => void;
}

/**
 * Chat input component with support for:
 * - Text input with file attachments
 * - Model selection
 * - Clear history button
 * - Tool action menu
 */
export function ChatInput({
  model,
  onModelChange,
  onSubmit,
  status,
  onStop,
  messages,
  onClearHistory,
  isEnabled,
  showDebug = false,
  onManualToolCall,
  isDebugDrawerOpen = false,
  onToggleDebugDrawer,
  policyContextAvailable,
  onClearPolicyContext
}: ChatInputProps) {
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const submitInFlightRef = useRef(false);

  const pendingToolCallConfirmation = messages.some((m: UIMessage) =>
    m.parts?.some(
      (part) =>
        isToolUIPart(part) &&
        part.state === "input-available" &&
        toolsRequiringConfirmation.includes(
          part.type.replace("tool-", "") as keyof typeof tools
        )
    )
  );
  const isPolicyReviewActive = isPolicyContextAvailable(policyContextAvailable);
  const effectiveStatus =
    status === "ready" && isSubmitPending ? "submitted" : status;
  const isChatBusy = isChatBusyStatus(effectiveStatus);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage, event: FormEvent<HTMLFormElement>) => {
      if (isChatBusy || submitInFlightRef.current) {
        onStop();
        return;
      }

      submitInFlightRef.current = true;
      setIsSubmitPending(true);

      try {
        await onSubmit(message, event);
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitPending(false);
      }
    },
    [isChatBusy, onStop, onSubmit]
  );

  const handleTextareaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing &&
        isChatBusy
      ) {
        event.preventDefault();
        onStop();
      }
    },
    [isChatBusy, onStop]
  );

  return (
    <div className="space-y-2">
      <PromptInput
        onSubmit={handleSubmit}
        className="mt-2"
        globalDrop
        multiple
      >
        <PromptInputHeader>
          <PromptAttachmentsDisplay />
          {isPolicyReviewActive ? (
            <div className="ml-auto flex items-center gap-1.5">
              <span
                className="inline-flex items-center rounded-full border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                title="Policy review context is available for follow-up questions."
              >
                {POLICY_REVIEW_ACTIVE_LABEL}
              </span>
              <PromptInputButton
                aria-label="Clear policy review context"
                className="h-6 px-2 text-[11px]"
                onClick={() => invokePolicyContextClear(onClearPolicyContext)}
                variant="ghost"
              >
                Clear
              </PromptInputButton>
            </div>
          ) : null}
        </PromptInputHeader>
        <PromptInputBody>
          <PromptInputTextarea
            className="min-h-12 max-h-36"
            onKeyDown={handleTextareaKeyDown}
            placeholder="What would you like to know?"
          />
        </PromptInputBody>
        <PromptInputFooter className="gap-0.5">
          <PromptInputTools>
            {isEnabled("tool-actions") && (
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger />
                <PromptInputActionMenuContent>
                  {isEnabled("file-attachments") && (
                    <PromptInputActionAddAttachments />
                  )}
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
            )}

            {isEnabled("model-selection") && (
              <PromptInputSelect onValueChange={onModelChange} value={model}>
                <PromptInputSelectTrigger>
                  <PromptInputSelectValue />
                </PromptInputSelectTrigger>
                <PromptInputSelectContent>
                  {AVAILABLE_MODELS.map((modelConfig) => (
                    <PromptInputSelectItem
                      key={modelConfig.id}
                      value={modelConfig.id}
                    >
                      {modelConfig.name}
                    </PromptInputSelectItem>
                  ))}
                </PromptInputSelectContent>
              </PromptInputSelect>
            )}

            {isEnabled("clear-history") && (
              <PromptInputButton
                disabled={messages.length === 0}
                onClick={onClearHistory}
                variant="ghost"
              >
                <Trash size={16} />
                <span>Clear</span>
              </PromptInputButton>
            )}
          </PromptInputTools>

          <div className="flex items-center gap-1">
            {showDebug && (
              <Suspense fallback={null}>
                <FeatureFlagsDebug />
              </Suspense>
            )}
            {showDebug && onManualToolCall && (
              <Suspense fallback={null}>
                <ManualToolCallsDebug onRunTool={onManualToolCall} />
              </Suspense>
            )}
            {showDebug && onToggleDebugDrawer && (
              <Suspense fallback={null}>
                <DebugTimelineToggle
                  onToggle={onToggleDebugDrawer}
                  open={isDebugDrawerOpen}
                />
              </Suspense>
            )}
            <PromptInputSubmit
              disabled={pendingToolCallConfirmation}
              onStop={onStop}
              status={effectiveStatus}
            />
          </div>
        </PromptInputFooter>
      </PromptInput>
    </div>
  );
}
